import {
  OverflowGradientStyles,
  TrendChartWrapperStyles,
} from "./TrendChartStyles";
import TrendChartContent from "./TrendChartContent/TrendChartContent";

import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import {
  useLoadWhenValuesChange,
  useFocusRange,
  useGetPageFromPath,
  useInterval,
} from "utils/customHooks/genericHooks";
import { useIsStorybook } from "utils/customHooks/storyHooks";

import { useSelector } from "store/useSelector";

import TrendChartLoaderSkeleton from "./TrendChartLoaderSkeleton";
import { getIsCypressRunning, getIsIe11 } from "../../../constants/constants";
import { useIsTrendChartStillLoading } from "../../../pages/DashboardPages/LoadingIndicatorDashboardPages";
import ErrorBoundary from "../../SimpleComponents/Errors/ErrorBoundary";
import { TREND_CHART_CONFIG } from "../../../constants/customers";
import React, { useEffect, useMemo, useState } from "react";
import { StyledLinearProgressWithTooltipArrow } from "components/SimpleComponents/LoadingLinearProgress";
import { useIsDarkMode } from "store/atoms";
import { useLocation } from "react-router";
import { useObserveComponentLoadTime } from "App/FlightPerformanceObserver";
import { usePrevious } from "react-use";

type TrendChartProps = {
  isLoaderAnimationEnabled?: boolean;
};

/** check whether all data has arrived on this interval */
const TREND_CHART_LOADING_INTERVAL = 500;

const TrendChart = React.memo<TrendChartProps>(
  ({ isLoaderAnimationEnabled: isLoaderAnimationEnabledProp = true }) => {
    const focusRange = useFocusRange();
    const { isGridLayoutPage, isTrendsPage, isHubletPage } =
      useGetPageFromPath();

    const {
      isLoading: isStillLoadingData,
      percentLoaded,
      numerator,
      denominator,
      explanation,
    } = useIsTrendChartStillLoading();

    const shouldFetchNewShifts = useSelector(
      state => state.legacy.shouldFetchNewShifts
    );
    const trendChartBinSize = useSelector(
      state => state.legacy.trendChartBinSize
    );
    const trendsPageFilterState = useSelector(
      state => state.legacy.trendsPageFilterState
    );
    const ckey = useCustomerKey();

    const isFetchingNewShifts = shouldFetchNewShifts && !isTrendsPage;

    const trendsPageFilterStateWithoutCkey = useMemo(
      () => ({
        // listen to filter state, but
        ...trendsPageFilterState,
        // prevent reload on first left drawer open (when process ckey is populated)
        process: { ...trendsPageFilterState.process, ckey: "" },
      }),
      [trendsPageFilterState]
    );
    const isStorybook = useIsStorybook();
    const isLoaderAnimationEnabled =
      isLoaderAnimationEnabledProp && !isStorybook;
    const location = useLocation();
    const didChangeRecently = useLoadWhenValuesChange({
      valuesWhichChangeArr: [
        focusRange && focusRange.ge, // focus range lt may keep changing if looking at today
        trendChartBinSize,
        isLoaderAnimationEnabled,
        isFetchingNewShifts,
        trendsPageFilterStateWithoutCkey,
        isStillLoadingData,
        location.pathname,
      ],
      loadingInterval:
        TREND_CHART_LOADING_INTERVAL *
        // load slower on trends page
        // except during cypress tests
        (isTrendsPage && !getIsCypressRunning() ? 1.5 : 1),
      isInitiallyLoading: false,
      componentNameForDebugging: "TrendChart",
    });

    const isIE = getIsIe11();
    const isLoading = (isTrendsPage && didChangeRecently) || isStillLoadingData;

    useObserveComponentLoadTime({
      name: "Trend Chart",
      loading: isStillLoadingData,
    });

    const [showLoader, setShowLoader] = useState(isLoading);

    // load again when the focus range or url changes,
    const prevPathname = usePrevious(location.pathname);
    useEffect(() => {
      // unless we're switching to process-tiles page and back i.e. pathname didn't change = data is already loaded
      if (isLoading && prevPathname !== location.pathname) {
        setShowLoader(true);
      }
    }, [focusRange, location.pathname, isLoading, prevPathname]);

    // stop loading animation if we've loaded once (i.e. when data is refetched every interval, don't show loader again)
    useInterval({
      callback: () => {
        if (!isLoading) {
          setTimeout(() => {
            setShowLoader(false);
          }, 2000); // delay a bit to avoid interrupting the "loaded" animation
        }
      },
      delay: showLoader ? TREND_CHART_LOADING_INTERVAL : 0,
      immediate: false,
    });

    const shouldShowOverflowGradient = !showLoader;
    const [isDarkMode] = useIsDarkMode();
    return (
      <ErrorBoundary id="TrendChart">
        <div style={{ height: "100%", position: "relative", width: "100%" }}>
          {showLoader ? (
            <StyledLinearProgressWithTooltipArrow
              variant={percentLoaded > 0 ? "determinate" : "indeterminate"}
              value={percentLoaded * 100}
              numerator={numerator}
              denominator={isNaN(denominator) ? 0 : denominator}
              explanation={explanation}
            />
          ) : null}
          {shouldShowOverflowGradient ? (
            <OverflowGradientStyles
              className="OverflowGradientStyles"
              $isDarkMode={isDarkMode}
            />
          ) : null}
          <TrendChartWrapperStyles
            data-component-name={"Trend Chart"}
            {...{
              $isGridLayoutPage: isGridLayoutPage,
              $isLoading: showLoader,
              $hasLegend: Boolean(TREND_CHART_CONFIG[ckey]?.categories),
              className: `trendChartWrapper${
                isHubletPage
                  ? " detailsPage"
                  : isTrendsPage
                    ? " trendsPage"
                    : isGridLayoutPage
                      ? " gridPage"
                      : ""
              }`,
            }}
          >
            {isLoaderAnimationEnabled && showLoader ? (
              <TrendChartLoaderSkeleton
                className={`${isIE ? "internetExplorer" : ""}`}
              />
            ) : (
              <TrendChartContent
                {...{
                  isIE,
                  isLoaderAnimationEnabled,
                }}
              />
            )}
          </TrendChartWrapperStyles>
        </div>
      </ErrorBoundary>
    );
  }
);

export default TrendChart;
